import * as Chart from 'chart.js';
import { helpers, defaults, elements, controllers } from 'chart.js';
import { createPopper } from '@popperjs/core';

var CDS_CHARTJS_COLORS = {
    lilac: '#4a5de2',
    green: '#37ca93',
    strawberryMilk: '#fa7f7f',
    sand: '#fdd38d',
    arctic: '#13d3ff',
    blush: '#fcaefb',
    skyBlue: '#004c92',
    greenApple: '#85bc83',
    darkGreen: '#008945',
    darkRed: '#dc2746'
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

var CHART_COLORS = {
    white: '#ffffff',
    gridLine: '#e3e3fa',
    gray: '#959595',
    black: '#000000',
    red: '#ff0000',
    darkBlue: '#283593',
};

var DEFAULT_COLORS = Object.values(CDS_CHARTJS_COLORS);
var LineChartColor = /** @class */ (function () {
    function LineChartColor() {
        this.colorUsed = 0;
        this.colors = [];
    }
    LineChartColor.prototype.getColor = function (index) {
        return this.colors[index] ? this.colors[index] : this.getNextColor();
    };
    LineChartColor.prototype.userColors = function (colors) {
        var _this = this;
        colors.forEach(function (color, index) {
            _this.colors[index] = color ? color : _this.getNextColor();
        });
    };
    LineChartColor.prototype.getNextColor = function () {
        if (this.colorUsed >= DEFAULT_COLORS.length) {
            this.colorUsed = 0;
        }
        return DEFAULT_COLORS[this.colorUsed++];
    };
    return LineChartColor;
}());

function isChartPlugins(chart) {
    return '$plugins' in chart;
}
function isChartScale(chart) {
    return 'scales' in chart;
}
function isChartTooltip(chart) {
    return 'tooltip' in chart;
}
function isIndexableOption(option) {
    return Array.isArray(option);
}
function isBarModel(model) {
    return ['horizontal', 'width', 'height', 'base', 'x', 'y'].every(function (prop) { return prop in model; });
}
function isFadeColor(color) {
    return 'fadeColor' in color;
}
function isMixedColor(color) {
    return 'first' in color;
}
function isTrendColor(color) {
    return 'trendColor' in color;
}

var SQR_TWO = 1.414213;
var DOUGHNUT_INNER_RADIUS = new WeakMap();
var SHADOW_DOUGHNUT = new WeakMap();
var DoughnutChartPlugin = /** @class */ (function () {
    function DoughnutChartPlugin(userConfig) {
        this.id = 'cdsDoughnutChartPlugin';
        this.colors = new LineChartColor();
        this.config = {
            growOnHover: 10
        };
        this.afterResizeAndInitial = true;
        this.config = __assign(__assign({}, this.config), userConfig);
    }
    DoughnutChartPlugin.prototype.beforeInit = function (chart) {
        var _this = this;
        this.chart = chart;
        chart.data.datasets.forEach(function (dataset) {
            if (dataset.backgroundColor instanceof Array || !dataset.backgroundColor) {
                dataset.backgroundColor = dataset.data.map(function (data, index) {
                    return dataset.backgroundColor && dataset.backgroundColor[index] ? dataset.backgroundColor[index] : _this.colors.getColor(index);
                });
            }
        });
        if (chart.canvas.parentElement.classList.contains('cds-chartjs-wrapper')) {
            this.cdsChartjsDoughnut = chart.canvas.parentElement.querySelector('.cds-chartjs-doughnut');
        }
        chart.data.datasets.forEach(function (dataset) {
            dataset.hoverBackgroundColor = dataset.backgroundColor;
        });
        var padding = chart.options.layout.padding;
        if (typeof padding !== 'number') {
            if (padding.top === 0 && padding.right === 0 && padding.bottom === 0 && padding.left === 0) {
                chart.options.layout.padding = this.config.growOnHover * 2;
            }
        }
        chart.options.onHover = function (_, activeElements) {
            chart.update();
            if (activeElements && activeElements[0]) {
                activeElements[0]._model.outerRadius += activeElements.length ? _this.config.growOnHover : -_this.config.growOnHover;
                SHADOW_DOUGHNUT.set(chart, {
                    dataIndex: activeElements[0]._index,
                    datasetIndex: activeElements[0]._datasetIndex,
                });
                _this.activeElement = {
                    dataIndex: activeElements[0]._index,
                    datasetIndex: activeElements[0]._datasetIndex
                };
            }
            else {
                _this.activeElement = null;
                SHADOW_DOUGHNUT.set(chart, null);
            }
        };
    };
    DoughnutChartPlugin.prototype.afterInit = function (chart) {
        chart.canvas.parentElement.style.height = chart.canvas.offsetHeight + "px";
    };
    DoughnutChartPlugin.prototype.beforeDatasetsDraw = function (chart) {
        var _this = this;
        var datasets = chart.data.datasets;
        var metasets = datasets.map(function (el, i) { return chart.getDatasetMeta(i); });
        if (!this.notify(chart, 'beforeDatasetsDraw', true)) {
            return;
        }
        metasets.forEach(function (meta, i) { return _this.drawDataset(chart, meta, i); });
        this.notify(chart, 'afterDatasetsDraw', false);
        return false;
    };
    DoughnutChartPlugin.prototype.resize = function () {
        this.afterResizeAndInitial = true;
    };
    DoughnutChartPlugin.prototype.afterDraw = function (chart) {
        if (this.cdsChartjsDoughnut && this.afterResizeAndInitial) {
            var size = DOUGHNUT_INNER_RADIUS.get(chart) * SQR_TWO;
            chart.canvas.parentElement.style.height = chart.canvas.offsetHeight + "px";
            this.cdsChartjsDoughnut.style.width = size + "px";
            this.cdsChartjsDoughnut.style.height = size + "px";
            this.afterResizeAndInitial = false;
        }
    };
    DoughnutChartPlugin.prototype.notify = function (chart, hook, notifyInProgress, meta, index) {
        if (isChartPlugins(chart)) {
            var descriptors = chart.$plugins.descriptors;
            var allowNotify_1 = !notifyInProgress;
            descriptors.forEach(function (descriptor) {
                var plugin = descriptor.plugin;
                if (plugin instanceof DoughnutChartPlugin) {
                    allowNotify_1 = true;
                }
                else if (allowNotify_1) {
                    var method = plugin[hook];
                    if (typeof method === 'function') {
                        var params = [];
                        params.push(chart);
                        if (meta && index) {
                            params.push({ meta: meta, index: index });
                        }
                        params.push(descriptor.options);
                        if (method.apply(plugin, params) === false) {
                            return false;
                        }
                    }
                }
            });
        }
        return true;
    };
    DoughnutChartPlugin.prototype.drawDataset = function (chart, meta, index) {
        var _a, _b;
        if (!this.notify(chart, 'beforeDatasetDraw', false, meta, index)) {
            return;
        }
        var data = meta.data, controller = meta.controller;
        if (this.activeElement) {
            _a = [data[data.length - 1], data[this.activeElement.dataIndex]], data[this.activeElement.dataIndex] = _a[0], data[data.length - 1] = _a[1];
            controller.draw();
            _b = [data[data.length - 1], data[this.activeElement.dataIndex]], data[this.activeElement.dataIndex] = _b[0], data[data.length - 1] = _b[1];
        }
        else {
            controller.draw();
        }
        this.notify(chart, 'afterDatasetDraw', false, meta, index);
    };
    DoughnutChartPlugin.prototype.isHover = function (element) {
        return !!(this.activeElement && element.dataIndex === this.activeElement.dataIndex && element.datasetIndex === this.activeElement.datasetIndex && SHADOW_DOUGHNUT.get(this.chart) != null);
    };
    return DoughnutChartPlugin;
}());

var LegendPointColorMode;
(function (LegendPointColorMode) {
    LegendPointColorMode["First"] = "first";
    LegendPointColorMode["Mixed"] = "mixed";
})(LegendPointColorMode || (LegendPointColorMode = {}));

var LEGEND_COLOR = new WeakMap();
var LegendPlugin = /** @class */ (function () {
    function LegendPlugin(userConfig) {
        this.config = {
            customContent: null,
            element: null,
            labelTransform: null,
            pointColorMode: LegendPointColorMode.Mixed
        };
        this.config = __assign(__assign({}, this.config), userConfig);
        this.legendElementTemplate = this.createLegendElementTemplate();
    }
    LegendPlugin.prototype.beforeInit = function (chart) {
        chart.config.options.legend.display = false;
    };
    LegendPlugin.prototype.createLegendElementTemplate = function () {
        var legendElement = document.createElement('li');
        legendElement.classList.add('cds-chartjs-legend-element');
        var circle = document.createElement('span');
        circle.classList.add('cds-chartjs-legend__circle');
        var label = document.createElement('span');
        label.classList.add('cds-chartjs-legend__label');
        legendElement.appendChild(circle);
        legendElement.appendChild(label);
        return legendElement;
    };
    LegendPlugin.prototype.afterLayout = function (chart) {
        var _this = this;
        if (!this.config.element) {
            throw new Error('no parent element for legend');
        }
        var legendDestinationElement = this.config.element;
        var datasets = chart.data.datasets;
        var isDoughnut = chart.config.plugins.filter(function (plugin) { return plugin instanceof DoughnutChartPlugin; }).length;
        var hasCustomContent = this.config.customContent;
        if (isDoughnut || !hasCustomContent) {
            if (!this.legendContainer) {
                this.legendContainer = document.createElement('ul');
                this.legendContainer.classList.add('cds-chartjs-legend__list');
                legendDestinationElement.appendChild(this.legendContainer);
            }
            var children_1 = this.legendContainer.children;
            var childrenLength = children_1.length;
            var legendElementsAmount_1 = 0;
            if (isDoughnut) {
                datasets.forEach(function (dataset) {
                    legendElementsAmount_1 += dataset.data.length;
                });
            }
            else {
                legendElementsAmount_1 = datasets.length;
            }
            if (childrenLength < legendElementsAmount_1) {
                for (var i = 0; i < legendElementsAmount_1 - childrenLength; i++) {
                    this.legendContainer.appendChild(this.legendElementTemplate.cloneNode(true));
                }
            }
            else if (childrenLength > legendElementsAmount_1) {
                for (var i = 0; i < childrenLength - legendElementsAmount_1; i++) {
                    this.legendContainer.removeChild(children_1[childrenLength - 1 - i]);
                }
            }
            var childIndex_1 = 0;
            if (isDoughnut) {
                datasets.forEach(function (dataset, datasetIndex) {
                    dataset.data.forEach(function (value, index) {
                        var element = children_1[childIndex_1++];
                        var circle = element.querySelector('.cds-chartjs-legend__circle');
                        var label = element.querySelector('.cds-chartjs-legend__label');
                        var shadow = SHADOW_DOUGHNUT.get(chart);
                        if (shadow != null && shadow.dataIndex === index && shadow.datasetIndex === datasetIndex) {
                            element.classList.add('cds-chartjs-legend-element--active');
                        }
                        else {
                            element.classList.remove('cds-chartjs-legend-element--active');
                        }
                        circle.style.backgroundColor = dataset.backgroundColor[index];
                        label.innerText = _this.config.labelTransform ? _this.config.labelTransform(chart.data.labels[index]) : String(chart.data.labels[index]);
                    });
                });
            }
            else {
                datasets.forEach(function (dataset) {
                    var element = children_1[childIndex_1++];
                    var circle = element.querySelector('.cds-chartjs-legend__circle');
                    var label = element.querySelector('.cds-chartjs-legend__label');
                    circle.style.cssText = _this.getTooltipCircleStyle(dataset);
                    label.innerText = _this.config.labelTransform ? _this.config.labelTransform(dataset.label) : dataset.label;
                });
            }
        }
        else {
            legendDestinationElement.innerHTML = '';
            legendDestinationElement.appendChild(this.config.customContent(datasets));
        }
    };
    LegendPlugin.prototype.getTooltipCircleStyle = function (dataset) {
        var color = LEGEND_COLOR.get(dataset);
        if (typeof color === 'string') {
            return "background-color: " + color;
        }
        else {
            if (isFadeColor(color)) {
                return "background-color: " + color.fadeColor;
            }
            if (isMixedColor(color)) {
                if (this.config.pointColorMode === LegendPointColorMode.First)
                    return "background-color: " + color.first.fadeColor;
                else {
                    var gradient = "linear-gradient(" + color.first.fadeColor + " 0%, " + color.first.fadeColor + " 50%, " + color.second.fadeColor + " 50%, " + color.second.fadeColor + " 100%)";
                    return "background-image: " + gradient;
                }
            }
        }
    };
    return LegendPlugin;
}());

function canFit(context) {
    var meta = context.chart.getDatasetMeta(context.datasetIndex);
    var view = meta.data[context.dataIndex]._view;
    if (isBarModel(view)) {
        var horizontal = view.horizontal;
        var labelOptions = context.chart.options.plugins.datalabels;
        var barCoords = getBarCoordinates(view, horizontal);
        var size = getLabelSize(context);
        var anchor = getLabelAnchor(barCoords, horizontal, getIndexableAndScriptable(labelOptions.anchor, context));
        var center = getLabelCenter(getIndexableAndScriptable(labelOptions.offset, context), getIndexableAndScriptable(labelOptions.align, context), size, anchor, horizontal);
        var labelCoords = getLabelCoordinates(center, size, getIndexableAndScriptable(labelOptions.rotation, context));
        for (var i = 0; i < labelCoords.length; i++) {
            if (labelCoords[i].x < barCoords[0].x || labelCoords[i].x > barCoords[1].x || labelCoords[i].y < barCoords[0].y || labelCoords[i].y > barCoords[1].y) {
                return false;
            }
        }
        return true;
    }
    else {
        return false;
    }
}
function getBarCoordinates(view, horizontal) {
    var x = 0;
    var y = 0;
    var dx = 0;
    var dy = 0;
    if (horizontal) {
        x = Math.min(view.x, view.base);
        dx = Math.abs(view.base - view.x);
        y = view.y;
        dy = view.height;
    }
    else {
        x = view.x;
        dx = view.width;
        y = Math.min(view.y, view.base);
        dy = Math.abs(view.base - view.y);
    }
    return [{ x: x, y: y }, { x: x + dx, y: y + dy }];
}
function getLabelSize(context) {
    var labelOptions = context.chart.options.plugins.datalabels;
    var formatterFn = labelOptions.formatter;
    var value = context.dataset.data[context.dataIndex];
    var formattedData = formatterFn(value, context);
    var lines = getLines(formattedData);
    var textSize = getTextSize(lines, context);
    var padding = normalizePadding(getIndexableAndScriptable(labelOptions.padding, context));
    return {
        width: padding.left + textSize.width + padding.right,
        height: padding.top + textSize.height + padding.bottom
    };
}
function getLines(formattedData) {
    var lines = [];
    if (Array.isArray(formattedData)) {
        formattedData.forEach(function (el) {
            lines.push.apply(lines, getLines(el));
        });
    }
    else if (typeof formattedData === 'string') {
        formattedData.split('\n').forEach(function (line) {
            lines.push(line);
        });
    }
    else if (!helpers.isNullOrUndef(formattedData)) {
        lines.push(String(formattedData));
    }
    return lines;
}
function getTextSize(lines, context) {
    var font = context.chart.options.plugins.datalabels.font;
    var ctx = context.chart.ctx;
    var prevFont = ctx.font;
    ctx.font = helpers.fontString(font.size, font.style, font.family);
    var width = 0;
    lines.forEach(function (line) {
        width = Math.max(ctx.measureText(line).width, width);
    });
    ctx.font = prevFont;
    var size = font.size ? font.size : defaults.global.defaultFontSize;
    return {
        width: width,
        height: lines.length * Number(font.lineHeight) * size
    };
}
function normalizePadding(padding) {
    if (typeof padding === 'number') {
        return {
            top: padding,
            right: padding,
            bottom: padding,
            left: padding
        };
    }
    else {
        return {
            top: padding.top || 0,
            right: padding.right || 0,
            bottom: padding.bottom || 0,
            left: padding.left || 0
        };
    }
}
function getLabelAnchor(barCoords, horizontal, anchorType) {
    if (anchorType === void 0) { anchorType = 'center'; }
    if (horizontal) {
        var y = (barCoords[0].y + barCoords[1].y) / 2;
        switch (anchorType) {
            case 'start':
                return {
                    x: barCoords[0].x,
                    y: y
                };
            case 'center':
                return {
                    x: (barCoords[0].x + barCoords[1].x) / 2,
                    y: y
                };
            case 'end':
                return {
                    x: barCoords[1].x,
                    y: y
                };
        }
    }
    else {
        var x = (barCoords[0].x + barCoords[1].x) / 2;
        switch (anchorType) {
            case 'start':
                return {
                    x: x,
                    y: barCoords[1].y
                };
            case 'center':
                return {
                    x: x,
                    y: (barCoords[0].y + barCoords[1].y) / 2
                };
            case 'end':
                return {
                    x: x,
                    y: barCoords[0].y
                };
        }
    }
}
function getLabelCenter(offset, align, size, anchor, horizontal) {
    if (align === void 0) { align = 'center'; }
    switch (align) {
        case 'start':
            if (horizontal) {
                return {
                    x: anchor.x - size.width / 2 - offset,
                    y: anchor.y
                };
            }
            else {
                return {
                    x: anchor.x,
                    y: anchor.y + size.height / 2 + offset
                };
            }
        case 'center':
            return {
                x: anchor.x,
                y: anchor.y
            };
        case 'end':
            if (horizontal) {
                return {
                    x: anchor.x + size.width / 2 + offset,
                    y: anchor.y
                };
            }
            else {
                return {
                    x: anchor.x,
                    y: anchor.y - size.height / 2 - offset
                };
            }
        case 'top':
            return {
                x: anchor.x,
                y: anchor.y - size.height / 2 - offset
            };
        case 'right':
            return {
                x: anchor.x + size.width / 2 + offset,
                y: anchor.y
            };
        case 'bottom':
            return {
                x: anchor.x,
                y: anchor.y + size.height / 2 + offset
            };
        case 'left':
            return {
                x: anchor.x - size.width / 2 - offset,
                y: anchor.y
            };
    }
}
function getLabelCoordinates(center, size, rotation) {
    var width = size.width, height = size.height;
    return [getRotatedPoint(center, { x: -width / 2, y: -height / 2 }, rotation),
        getRotatedPoint(center, { x: width / 2, y: -height / 2 }, rotation),
        getRotatedPoint(center, { x: width / 2, y: height / 2 }, rotation),
        getRotatedPoint(center, { x: -width / 2, y: height / 2 }, rotation)];
}
function getRotatedPoint(center, point, angle) {
    var rad = Math.PI / 180 * angle;
    var cos = Math.cos(rad);
    var sin = Math.sin(rad);
    return {
        x: (cos * point.x) + (sin * point.y) + center.x,
        y: (cos * point.y) - (sin * point.x) + center.y
    };
}
function getIndexable(option, context) {
    if (Array.isArray(option)) {
        return option[context.dataIndex];
    }
    else {
        return option;
    }
}
function getScriptable(option, context) {
    if (typeof option === 'function') {
        return option(context);
    }
    else {
        return option;
    }
}
function getIndexableAndScriptable(option, context) {
    return getScriptable(getIndexable(option, context), context);
}

function getPointDisplayOption(option) {
    return typeof option === 'function' ? option : option === true;
}
function getPointOnHoverDisplay(datasets, element, pointsOnHover) {
    if (!isIndexableOption(pointsOnHover)) {
        return getPointDisplayOption(pointsOnHover);
    }
    else {
        if (datasets.length) {
            if (isIndexableOption(pointsOnHover[element._datasetIndex])) {
                var dataset = pointsOnHover[element._datasetIndex];
                return getPointDisplayOption(dataset[element._index]);
            }
            else {
                return getPointDisplayOption(pointsOnHover[element._datasetIndex]);
            }
        }
        else {
            return getPointDisplayOption(pointsOnHover[element._index]);
        }
    }
}

function hexToRGB(hex, alpha) {
    hex = hex.replace('#', '');
    if (hex.length !== 3 && hex.length !== 6) {
        throw new Error('invalid hex color');
    }
    if (hex.length === 3) {
        hex = "" + hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    var r = parseInt(hex.slice(0, 2), 16);
    var g = parseInt(hex.slice(2, 4), 16);
    var b = parseInt(hex.slice(4, 6), 16);
    if (alpha != null) {
        return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
    }
    else {
        return "rgb(" + r + ", " + g + ", " + b + ")";
    }
}

var zip = function (arrays) {
    return arrays[0].map(function (_, i) {
        return arrays.map(function (array) { return array[i]; });
    });
};
var RADIUS = new WeakMap();
var ROUNDED_BARS = new WeakMap();
var BarChartPlugin = /** @class */ (function () {
    function BarChartPlugin(userConfig) {
        this.id = 'cdsBarChartPlugin';
        this.DATASET_COLOR = new WeakMap();
        this.config = {
            datasetColors: null,
            highlightDatasetOnHover: true,
            pointsOnHover: false,
            radius: 5
        };
        this.lastActivePoints = [];
        this.lineChartColor = new LineChartColor();
        this.pointSteps = new WeakMap();
        this.config = __assign(__assign({}, this.config), userConfig);
    }
    BarChartPlugin.prototype.isSamePoint = function (pointA, pointB) {
        return (pointA._datasetIndex === pointB._datasetIndex) && (pointA._index === pointB._index);
    };
    BarChartPlugin.prototype.beforeInit = function (chart) {
        var _this = this;
        var _a;
        if ((_a = this.config.datasetColors) === null || _a === void 0 ? void 0 : _a.length) {
            this.lineChartColor.userColors(this.config.datasetColors);
        }
        chart.data.datasets.forEach(function (dataset, index) {
            if (dataset.backgroundColor) {
                _this.DATASET_COLOR.set(dataset, dataset.backgroundColor);
            }
            else if (_this.config.datasetColors && _this.config.datasetColors[index]) {
                _this.DATASET_COLOR.set(dataset, _this.config.datasetColors[index]);
            }
            else {
                _this.DATASET_COLOR.set(dataset, _this.lineChartColor.getNextColor());
            }
        });
        if (chart.options.scales.xAxes.filter(function (axis) { return axis.stacked; }).length) {
            var roundedBarDatasets = chart.data.datasets
                .map(function (dataset) {
                return dataset.data;
            });
            var roundedBars = zip(roundedBarDatasets).map(function (element) {
                return element.reduce(function (acc, element, index) {
                    if (element == null) {
                        return acc;
                    }
                    if (element >= 0) {
                        acc[0] = index;
                    }
                    else {
                        acc[1] = index;
                    }
                    return acc;
                }, [null, null]);
            });
            ROUNDED_BARS.set(chart, roundedBars);
        }
        else {
            ROUNDED_BARS.set(chart, true);
        }
        RADIUS.set(chart, this.config.radius);
        chart.data.datasets.forEach(function (dataset) {
            dataset.backgroundColor = _this.DATASET_COLOR.get(dataset);
            dataset.hoverBackgroundColor = dataset.hoverBackgroundColor || dataset.backgroundColor;
            LEGEND_COLOR.set(dataset, dataset.backgroundColor);
        });
    };
    BarChartPlugin.prototype.beforeDraw = function (chart) {
        if (this.config.highlightDatasetOnHover && this.left && isChartScale(chart)) {
            var _a = chart.chartArea, left = _a.left, right = _a.right, bottom = _a.bottom, top_1 = _a.top;
            var ctx = chart.ctx;
            var xScale = chart.scales['x-axis-0'];
            var tickWidth = (right - left) / xScale.ticks.length;
            var tickLeft = tickWidth * Math.floor(this.left / tickWidth);
            ctx.save();
            ctx.fillStyle = hexToRGB(CHART_COLORS.gridLine, 0.5);
            ctx.fillRect(left + tickLeft, top_1, tickWidth, bottom - top_1);
            ctx.restore();
        }
    };
    BarChartPlugin.prototype.beforeEvent = function (chart, event) {
        if (this.config.highlightDatasetOnHover && event.type.substr(0, 5) === 'mouse') {
            var area = chart.chartArea;
            var x = event.x, y = event.y;
            if (x > area.left && x < area.right && y > area.top && y < area.bottom) {
                this.left = x - area.left;
            }
            else {
                this.left = null;
            }
        }
    };
    BarChartPlugin.prototype.afterDatasetsDraw = function (chart, easing) {
        this.drawPointsOnHover(chart, Number(easing) / 10);
    };
    BarChartPlugin.prototype.drawPoint = function (chart, activeElement, stepPercent) {
        var ctx = chart.ctx;
        var x = activeElement.tooltipPosition().x;
        var y = ((activeElement._view.base > chart.chartArea.bottom ? chart.chartArea.bottom : activeElement._view.base) + activeElement._view.y) / 2;
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, 14, 0, 2 * Math.PI, false);
        ctx.fillStyle = "rgba(255, 255, 255, " + 0.5 * stepPercent + ")";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 7 * stepPercent, 0, 2 * Math.PI, false);
        ctx.fillStyle = activeElement._view.backgroundColor;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 5 * stepPercent, 0, 2 * Math.PI, false);
        ctx.fillStyle = CHART_COLORS.white;
        ctx.fill();
        ctx.restore();
    };
    BarChartPlugin.prototype.drawPointsOnHover = function (chart, stepDelta) {
        var _this = this;
        if (chart && isChartScale(chart) && isChartTooltip(chart)) {
            var active_1 = chart.tooltip._active ? chart.tooltip._active.filter(function (point) {
                var pointOnHoverDisplay = getPointOnHoverDisplay(chart.data.datasets, point, _this.config.pointsOnHover);
                var context = {
                    active: true,
                    chart: chart,
                    dataIndex: point._index,
                    dataset: chart.data.datasets[point._datasetIndex],
                    datasetIndex: point._datasetIndex
                };
                return typeof pointOnHoverDisplay === 'function' ? pointOnHoverDisplay(context) : pointOnHoverDisplay;
            }) : [];
            if (active_1 && active_1[0]) {
                var changed_1 = false;
                this.lastActivePoints.forEach(function (point) {
                    if (!active_1.some(function (activePoint) { return _this.isSamePoint(point, activePoint); })) {
                        _this.pointSteps.set(point, 0);
                        changed_1 = true;
                    }
                });
                active_1.forEach(function (point) {
                    if (!_this.lastActivePoints.some(function (activePoint) { return _this.isSamePoint(point, activePoint); })) {
                        changed_1 = true;
                    }
                    var lastStep = _this.pointSteps.get(point) || 0;
                    var currentStep = lastStep + stepDelta > 1 ? 1 : lastStep + stepDelta;
                    _this.pointSteps.set(point, currentStep);
                    _this.drawPoint(chart, point, currentStep);
                });
                if (changed_1) {
                    this.lastActivePoints = __spreadArrays(active_1);
                    chart.update();
                }
            }
            else {
                this.lastActivePoints.forEach(function (point) {
                    _this.pointSteps.set(point, 0);
                });
                if (this.lastActivePoints.length > 0) {
                    this.lastActivePoints = [];
                    chart.update();
                }
            }
        }
    };
    BarChartPlugin.prototype.fitInContext = function (context) {
        return canFit(context);
    };
    BarChartPlugin.prototype.isActive = function (context) {
        return this.lastActivePoints.some(function (e) { return e._datasetIndex == context.datasetIndex && e._index == context.dataIndex; });
    };
    return BarChartPlugin;
}());

var BAR_TYPE = 'cdsBar';
var useBarChartOnce = false;
function useBarChart() {
    if (useBarChartOnce) {
        return;
    }
    useBarChartOnce = true;
    // @ts-ignore
    var rectangle = elements.CdsRectangle = elements.Rectangle.extend({
        draw: function () {
            var roundedBars = ROUNDED_BARS.get(this._chart);
            var ctx = this._chart.ctx;
            var vm = this._view;
            var left = vm.x - vm.width / 2;
            var right = vm.x + vm.width / 2;
            var top = vm.y;
            var bottom = vm.base;
            ctx.beginPath();
            ctx.fillStyle = vm.backgroundColor;
            ctx.strokeStyle = vm.borderColor;
            var width = right - left;
            var height = bottom - top;
            var x = left;
            var y = top;
            var rad = RADIUS.get(this._chart);
            if (Math.abs(height) < rad) {
                rad = Math.abs(height);
            }
            else if (width < rad) {
                rad = width;
            }
            var roundedTop = roundedBars === true || roundedBars[this._index][0] === this._datasetIndex;
            var roundedBottom = roundedBars === true || roundedBars[this._index][1] === this._datasetIndex;
            if (roundedTop && height > 0) {
                ctx.moveTo(x + rad, y);
                ctx.lineTo(x + width - rad, y);
                ctx.quadraticCurveTo(x + width, y, x + width, y + rad);
                ctx.lineTo(x + width, y + height - rad);
                ctx.lineTo(x + width, y + height);
                ctx.lineTo(x + rad, y + height);
                ctx.lineTo(x, y + height);
                ctx.lineTo(x, y + rad);
                ctx.quadraticCurveTo(x, y, x + rad, y);
            }
            else if (roundedBottom && height < 0) {
                ctx.moveTo(x + width - rad, y);
                ctx.lineTo(x + rad, y);
                ctx.quadraticCurveTo(x, y, x, y - rad);
                ctx.lineTo(x, y + height);
                ctx.lineTo(x + width, y + height);
                ctx.lineTo(x + width, y - rad);
                ctx.quadraticCurveTo(x + width, y, x + width - rad, y);
            }
            else {
                ctx.moveTo(x, y);
                ctx.lineTo(x + width, y);
                ctx.lineTo(x + width, y + height);
                ctx.lineTo(x, y + height);
                ctx.lineTo(x, y);
            }
            ctx.fill();
        }
    });
    defaults[BAR_TYPE] = helpers.clone(defaults.bar);
    // needed in 2.9.x -> Chart.defaults.global.datasets.roundedBar = Chart.defaults.global.datasets.bar
    // @ts-ignore
    defaults.global.datasets[BAR_TYPE] = defaults.global.datasets.bar;
    controllers[BAR_TYPE] = controllers.bar.extend({
        dataElementType: rectangle,
    });
}

var TAU = Math.PI * 2;
var DOUGHNUT_TYPE = 'cdsDoughnut';
var useDoughnutChartOnce = false;
function useDoughnutChart() {
    if (useDoughnutChartOnce) {
        return;
    }
    useDoughnutChartOnce = true;
    // @ts-ignore
    var arc = elements.CdsArc = elements.Arc.extend({
        draw: function () {
            var ctx = this._chart.ctx;
            var vm = this._view;
            var pixelMargin = (vm.borderAlign === 'inner') ? 0.33 : 0;
            var arc = {
                x: vm.x,
                y: vm.y,
                innerRadius: vm.innerRadius,
                outerRadius: Math.max(vm.outerRadius - pixelMargin, 0),
                pixelMargin: pixelMargin,
                startAngle: vm.startAngle,
                endAngle: vm.endAngle,
                fullCircles: Math.floor(vm.circumference / TAU)
            };
            var i;
            DOUGHNUT_INNER_RADIUS.set(this._chart, vm.innerRadius);
            ctx.save();
            ctx.fillStyle = vm.backgroundColor;
            ctx.strokeStyle = vm.borderColor;
            if (arc.fullCircles) {
                arc.endAngle = arc.startAngle + TAU;
                ctx.beginPath();
                ctx.arc(arc.x, arc.y, arc.outerRadius, arc.startAngle, arc.endAngle);
                ctx.arc(arc.x, arc.y, arc.innerRadius, arc.endAngle, arc.startAngle, true);
                ctx.closePath();
                for (i = 0; i < arc.fullCircles; ++i) {
                    ctx.fill();
                }
                arc.endAngle = arc.startAngle + vm.circumference % TAU;
            }
            ctx.beginPath();
            ctx.arc(arc.x, arc.y, arc.outerRadius, arc.startAngle, arc.endAngle);
            ctx.arc(arc.x, arc.y, arc.innerRadius, arc.endAngle, arc.startAngle, true);
            ctx.closePath();
            var shadow = SHADOW_DOUGHNUT.get(this._chart);
            if (shadow != null && shadow.dataIndex === this._index && shadow.datasetIndex === this._datasetIndex) {
                ctx.shadowColor = 'rgba(0,0,0,0.3)';
                ctx.shadowBlur = 15;
            }
            ctx.fill();
            ctx.restore();
        }
    });
    defaults[DOUGHNUT_TYPE] = helpers.clone(defaults.doughnut);
    // @ts-ignore
    defaults.global.datasets[DOUGHNUT_TYPE] = defaults.global.datasets.doughnut;
    controllers[DOUGHNUT_TYPE] = controllers.doughnut.extend({
        dataElementType: arc,
    });
}

var GridMode;
(function (GridMode) {
    GridMode[GridMode["None"] = 0] = "None";
    GridMode[GridMode["VerticalRuler"] = 2] = "VerticalRuler";
    GridMode[GridMode["VerticalLines"] = 4] = "VerticalLines";
    GridMode[GridMode["HorizontalRuler"] = 8] = "HorizontalRuler";
    GridMode[GridMode["HorizontalLines"] = 16] = "HorizontalLines";
    GridMode[GridMode["ZeroLineStyles"] = 32] = "ZeroLineStyles";
    GridMode[GridMode["Default"] = 58] = "Default";
    GridMode[GridMode["Simple"] = 20] = "Simple";
})(GridMode || (GridMode = {}));

var BOTTOM_LINE_POSITION = new WeakMap();
var GridPlugin = /** @class */ (function () {
    function GridPlugin(userConfig) {
        this.id = 'cdsGridPlugin';
        this.config = {
            gridMode: GridMode.Default,
            gridLineColor: CHART_COLORS.gridLine,
            horizontalLabelPadding: 24,
            horizontalLinePadding: 16,
            verticalLabelPadding: 13,
            verticalLines: false,
            zeroLineBorderDash: [4, 2],
            zeroLineColor: CHART_COLORS.darkBlue,
            zeroLineWidth: 1
        };
        this.config = __assign(__assign({}, this.config), userConfig);
    }
    GridPlugin.prototype.beforeInit = function (chart) {
        this.setDefaultStyles(chart);
        if (this.config.gridMode & GridMode.HorizontalRuler) {
            BOTTOM_LINE_POSITION.set(chart, this.config.horizontalLabelPadding);
        }
    };
    GridPlugin.prototype.beforeDatasetsDraw = function (chart) {
        this.drawInterface(chart);
    };
    GridPlugin.prototype.setDefaultStyles = function (chart) {
        var _this = this;
        chart.options.tooltips.intersect = false;
        chart.options.scales.xAxes.forEach(function (el) {
            el.gridLines.color = _this.config.gridLineColor;
            el.gridLines.display = !!(_this.config.gridMode & GridMode.VerticalLines) || _this.config.verticalLines;
            el.gridLines.drawTicks = false;
            el.gridLines.zeroLineColor = _this.config.gridLineColor;
            el.ticks.padding = !(_this.config.gridMode & GridMode.HorizontalRuler) ? _this.config.horizontalLabelPadding : _this.config.horizontalLabelPadding + _this.config.horizontalLinePadding;
        });
        chart.options.scales.yAxes.forEach(function (el) {
            el.gridLines.display = true;
            el.gridLines.color = (_this.config.gridMode & GridMode.HorizontalLines) ? _this.config.gridLineColor : 'transparent';
            el.gridLines.drawBorder = false;
            el.gridLines.drawTicks = false;
            el.ticks.padding = _this.config.verticalLabelPadding;
            if (_this.config.gridMode & GridMode.ZeroLineStyles) {
                el.gridLines.zeroLineBorderDash = _this.config.zeroLineBorderDash;
                el.gridLines.zeroLineColor = _this.config.zeroLineColor;
                el.gridLines.zeroLineWidth = _this.config.zeroLineWidth;
            }
            else {
                el.gridLines.zeroLineColor = el.gridLines.color;
            }
        });
    };
    GridPlugin.prototype.drawInterface = function (chart) {
        if (isChartScale(chart)) {
            var _a = chart.chartArea, top_1 = _a.top, bottom = _a.bottom, left_1 = _a.left;
            if (this.config.gridMode & GridMode.VerticalRuler) {
                chart.ctx.save();
                chart.ctx.fillStyle = CHART_COLORS.black;
                chart.ctx.fillRect(left_1 - 1, top_1 - 0.5, 1, (bottom - top_1) + 1);
                var gap_1 = (bottom - top_1) / (chart.scales['y-axis-0'].ticks.length - 1);
                chart.scales['y-axis-0'].ticks.forEach(function (_, i) {
                    chart.ctx.fillRect(left_1, (top_1 + gap_1 * i) - 0.5, -8, 1);
                });
                chart.ctx.restore();
            }
            if (this.config.gridMode & GridMode.HorizontalRuler) {
                chart.ctx.save();
                chart.ctx.fillStyle = CHART_COLORS.gray;
                chart.ctx.fillRect(left_1, chart.chartArea.bottom + this.config.horizontalLabelPadding, chart.chartArea.right - chart.chartArea.left, 1);
                chart.ctx.restore();
            }
        }
    };
    return GridPlugin;
}());

var LineChartPlugin = /** @class */ (function () {
    function LineChartPlugin(userConfig) {
        this.id = 'cdsLineChartPlugin';
        this.currentHoverAnimationX = 0;
        this.temporaryHoverAnimationX = 0;
        this.DATASET_COLOR = new WeakMap();
        this.config = {
            datasetColors: null,
            gradientOpacityFrom: 0.6,
            gradientOpacityTo: 0.1,
            hoverAnimationDuration: 350,
            joinLine: false,
            joinLineColor: CHART_COLORS.red,
            joinLineWidth: 2,
            pointsOnHover: false,
            verticalLine: true,
            verticalLineWidth: 2,
        };
        this.lastStep = 0;
        this.lastPercent = 0;
        this.currentRulerOrigin = 0;
        this.currentRulerPosition = 0;
        this.lineChartColor = new LineChartColor();
        this.lastActivePoints = [];
        this.pointSteps = new WeakMap();
        this.config = __assign(__assign({}, this.config), userConfig);
    }
    LineChartPlugin.prototype.beforeInit = function (chart) {
        var _this = this;
        var _a;
        if ((_a = this.config.datasetColors) === null || _a === void 0 ? void 0 : _a.length) {
            this.lineChartColor.userColors(this.config.datasetColors);
        }
        chart.data.datasets.forEach(function (dataset, index) {
            _this.setDatasetColor(dataset, index);
        });
        chart.options.hover.animationDuration = this.config.hoverAnimationDuration;
    };
    LineChartPlugin.prototype.afterLayout = function (chart) {
        this.drawChart(chart);
    };
    LineChartPlugin.prototype.beforeDatasetsDraw = function (chart, easing) {
        var _a = this.config, verticalLine = _a.verticalLine, joinLine = _a.joinLine;
        if (verticalLine) {
            this.drawVerticalLineOnHover(chart, Number(easing));
        }
        if (joinLine) {
            this.drawJoinLine(chart);
        }
    };
    LineChartPlugin.prototype.beforeUpdate = function (chart) {
        var _this = this;
        chart.data.datasets.forEach(function (dataset, index) {
            if (!_this.DATASET_COLOR.get(dataset)) {
                _this.setDatasetColor(dataset, index);
            }
        });
    };
    LineChartPlugin.prototype.afterDatasetsDraw = function (chart, easing) {
        this.drawPointsOnHover(chart, Number(easing));
    };
    LineChartPlugin.prototype.setDatasetColor = function (dataset, index) {
        if (dataset.backgroundColor) {
            this.DATASET_COLOR.set(dataset, dataset.backgroundColor);
        }
        else if (this.config.datasetColors && this.config.datasetColors[index]) {
            this.DATASET_COLOR.set(dataset, this.config.datasetColors[index]);
        }
        else {
            this.DATASET_COLOR.set(dataset, this.lineChartColor.getNextColor());
        }
    };
    LineChartPlugin.prototype.drawChart = function (chart) {
        var _this = this;
        if (isChartScale(chart)) {
            var datasets = chart.config.data.datasets;
            datasets.forEach(function (dataset) {
                var _a, _b, _c, _d;
                var gradientColors = _this.DATASET_COLOR.get(dataset);
                dataset.borderWidth = (_a = dataset.borderWidth) !== null && _a !== void 0 ? _a : 1;
                dataset.pointBorderColor = (_b = dataset.pointBorderColor) !== null && _b !== void 0 ? _b : CHART_COLORS.white;
                dataset.pointBorderWidth = (_c = dataset.pointBorderWidth) !== null && _c !== void 0 ? _c : 1.5;
                dataset.pointRadius = (_d = dataset.pointRadius) !== null && _d !== void 0 ? _d : 5;
                LEGEND_COLOR.set(dataset, gradientColors);
                if (typeof gradientColors === 'string') {
                    dataset.backgroundColor = gradientColors;
                    dataset.borderColor = gradientColors;
                }
                else {
                    if (isFadeColor(gradientColors)) {
                        _this.fillFadeColor(chart, dataset, gradientColors);
                    }
                    if (isMixedColor(gradientColors)) {
                        if (isFadeColor(gradientColors.first) && isFadeColor(gradientColors.second)) {
                            _this.fillMixedColor(chart, dataset, gradientColors);
                        }
                    }
                    if (isTrendColor(gradientColors)) {
                        _this.fillTrendColor(chart, dataset, gradientColors);
                    }
                }
            });
        }
    };
    LineChartPlugin.prototype.drawVerticalLineOnHover = function (chart, stepPercent) {
        if (isChartScale(chart) && isChartTooltip(chart)) {
            if (chart.tooltip._active && chart.tooltip._active[0]) {
                var ctx = chart.ctx;
                var currentDestination = chart.tooltip._active[0].tooltipPosition().x;
                var topY = chart.chartArea.top;
                var bottomLine = BOTTOM_LINE_POSITION.get(chart) || 0;
                var bottomY = chart.chartArea.bottom + bottomLine;
                var triangleWidth = 10;
                var triangleHeight = 8;
                if (stepPercent < this.lastStep) {
                    this.currentRulerOrigin = this.currentRulerPosition;
                }
                this.currentRulerPosition = this.currentRulerOrigin + (currentDestination - this.currentRulerOrigin) * stepPercent;
                var gradient = ctx.createLinearGradient(0, chart.chartArea.top, 0, bottomY);
                gradient.addColorStop(0, hexToRGB(CDS_CHARTJS_COLORS.lilac, 0.2));
                gradient.addColorStop(1, hexToRGB(CDS_CHARTJS_COLORS.lilac));
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(this.currentRulerPosition, topY);
                ctx.lineTo(this.currentRulerPosition, bottomY);
                ctx.lineWidth = this.config.verticalLineWidth;
                ctx.strokeStyle = gradient;
                ctx.stroke();
                ctx.moveTo(this.currentRulerPosition - triangleWidth / 2, bottomY);
                ctx.lineTo(this.currentRulerPosition + triangleWidth / 2, bottomY);
                ctx.lineTo(this.currentRulerPosition, bottomY - triangleHeight);
                ctx.fillStyle = gradient;
                ctx.fill();
                ctx.restore();
                this.lastStep = stepPercent;
            }
        }
    };
    LineChartPlugin.prototype.drawPoint = function (chart, activeElement, stepPercent) {
        var ctx = chart.ctx;
        var x = activeElement.tooltipPosition().x;
        var y = activeElement.tooltipPosition().y;
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, 14, 0, 2 * Math.PI, false);
        ctx.fillStyle = "rgba(255, 255, 255, " + 0.5 * stepPercent + ")";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 7 * stepPercent, 0, 2 * Math.PI, false);
        ctx.fillStyle = this.getFillPointColor(chart, activeElement);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 5 * stepPercent, 0, 2 * Math.PI, false);
        ctx.fillStyle = CHART_COLORS.white;
        ctx.fill();
        ctx.restore();
    };
    LineChartPlugin.prototype.isSamePoint = function (pointA, pointB) {
        return (pointA._datasetIndex === pointB._datasetIndex) && (pointA._index === pointB._index);
    };
    LineChartPlugin.prototype.drawPointsOnHover = function (chart, stepPercent) {
        var _this = this;
        var delta = this.lastPercent > stepPercent ? stepPercent : stepPercent - this.lastPercent;
        this.lastPercent = stepPercent;
        if (chart && isChartScale(chart) && isChartTooltip(chart)) {
            var active_1 = chart.tooltip._active ? chart.tooltip._active.filter(function (point) {
                var pointOnHoverDisplay = getPointOnHoverDisplay(chart.data.datasets, point, _this.config.pointsOnHover);
                var context = {
                    active: true,
                    chart: chart,
                    dataIndex: point._index,
                    dataset: chart.data.datasets[point._datasetIndex],
                    datasetIndex: point._datasetIndex
                };
                return typeof pointOnHoverDisplay === 'function' ? pointOnHoverDisplay(context) : pointOnHoverDisplay;
            }) : [];
            if (active_1 && active_1[0]) {
                var changed_1 = false;
                this.lastActivePoints.forEach(function (point) {
                    if (!active_1.some(function (activePoint) { return _this.isSamePoint(point, activePoint); })) {
                        _this.pointSteps.set(point, 0);
                        changed_1 = true;
                    }
                });
                active_1.forEach(function (point) {
                    if (!_this.lastActivePoints.some(function (activePoint) { return _this.isSamePoint(point, activePoint); })) {
                        changed_1 = true;
                    }
                    var lastStep = _this.pointSteps.get(point) || 0;
                    var currentStep = lastStep + delta > 1 ? 1 : lastStep + delta;
                    _this.pointSteps.set(point, currentStep);
                    _this.drawPoint(chart, point, currentStep);
                });
                if (changed_1) {
                    this.lastActivePoints = __spreadArrays(active_1);
                    chart.update({
                        duration: this.config.hoverAnimationDuration,
                        lazy: true
                    });
                }
            }
            else {
                this.lastActivePoints.forEach(function (point) {
                    _this.pointSteps.set(point, 0);
                });
                if (this.lastActivePoints.length > 0) {
                    this.lastActivePoints = [];
                    chart.update({
                        duration: this.config.hoverAnimationDuration,
                        lazy: true
                    });
                }
            }
        }
    };
    LineChartPlugin.prototype.drawJoinLine = function (chart) {
        if (isChartScale(chart) && isChartTooltip(chart)) {
            if (chart.tooltip._active && chart.tooltip._active[0]) {
                var ctx = chart.ctx;
                var minmax_1 = [];
                var xPosition_1 = 0;
                chart.tooltip._active.forEach(function (dataset) {
                    var x = dataset.tooltipPosition().x;
                    var y = dataset.tooltipPosition().y;
                    xPosition_1 = x;
                    minmax_1.push(y);
                });
                var min = Math.min.apply(Math, minmax_1);
                var max = Math.max.apply(Math, minmax_1);
                var joinLineWidth = this.config.joinLineWidth;
                ctx.save();
                ctx.fillStyle = this.config.joinLineColor;
                ctx.fillRect(xPosition_1 - joinLineWidth / 2, max, joinLineWidth, min - max);
                ctx.restore();
            }
        }
    };
    LineChartPlugin.prototype.fillFadeColor = function (chart, dataset, gradientColors) {
        var _a, _b;
        var ctx = chart.ctx, _c = chart.chartArea, top = _c.top, bottom = _c.bottom;
        var gradient = ctx.createLinearGradient(0, top, 0, bottom - top);
        gradient.addColorStop(0, hexToRGB(gradientColors.fadeColor, (_a = gradientColors.from) !== null && _a !== void 0 ? _a : this.config.gradientOpacityFrom));
        gradient.addColorStop(1, hexToRGB(gradientColors.fadeColor, (_b = gradientColors.to) !== null && _b !== void 0 ? _b : this.config.gradientOpacityTo));
        dataset.borderColor = gradientColors.fadeColor;
        dataset.pointBackgroundColor = gradientColors.fadeColor;
        dataset.backgroundColor = gradient;
    };
    LineChartPlugin.prototype.fillTrendColor = function (chart, dataset, gradientColors) {
        var _a, _b;
        var ctx = chart.ctx, _c = chart.chartArea, top = _c.top, right = _c.right, bottom = _c.bottom, left = _c.left;
        var gradient = ctx.createLinearGradient(0, top, 0, bottom - top);
        gradient.addColorStop(0, hexToRGB(gradientColors.primaryColor.fadeColor, (_a = gradientColors.primaryColor.from) !== null && _a !== void 0 ? _a : this.config.gradientOpacityFrom));
        gradient.addColorStop(1, hexToRGB(gradientColors.primaryColor.fadeColor, (_b = gradientColors.primaryColor.to) !== null && _b !== void 0 ? _b : this.config.gradientOpacityTo));
        var gradientBorder = ctx.createLinearGradient(left, 0, right, 0);
        gradientBorder.addColorStop(0, 'white');
        gradientBorder.addColorStop(0.1, gradientColors.primaryColor.fadeColor);
        gradientBorder.addColorStop(0.8, gradientColors.primaryColor.fadeColor);
        gradientBorder.addColorStop(1, gradientColors.trendColor);
        dataset.borderColor = gradientBorder;
        dataset.pointBackgroundColor = gradientColors.primaryColor.fadeColor;
        dataset.backgroundColor = gradient;
    };
    LineChartPlugin.prototype.fillMixedColor = function (chart, dataset, gradientColors) {
        var _a, _b, _c, _d;
        if (isChartScale(chart)) {
            var ctx = chart.ctx, top_1 = chart.chartArea.top, _e = chart.scales["y-axis-0"], minTickChart = _e.min, maxTickChart = _e.max, height = _e.height;
            var maxTick = Math.abs(maxTickChart);
            var minTick = Math.abs(minTickChart);
            var maxTickAbs = Math.max(maxTick, minTick);
            var zeroLinePositionPx = (maxTick * height) / (maxTick + minTick);
            var gradientHalfHeight = (maxTickAbs * height) / (maxTick + minTick);
            var gradientStart = zeroLinePositionPx - gradientHalfHeight + top_1;
            var gradientStop = zeroLinePositionPx + gradientHalfHeight + top_1;
            var gradientColorBreakPoint = 0.5;
            if (minTickChart >= 0 && maxTickChart > 0) {
                gradientColorBreakPoint = 1;
            }
            else if (minTickChart < 0 && maxTickChart <= 0) {
                gradientColorBreakPoint = 0;
            }
            var gradient = ctx.createLinearGradient(0, gradientStart, 0, gradientStop);
            gradient.addColorStop(0, hexToRGB(gradientColors.first.fadeColor, (_a = gradientColors.first.from) !== null && _a !== void 0 ? _a : this.config.gradientOpacityFrom));
            gradient.addColorStop(gradientColorBreakPoint, hexToRGB(gradientColors.first.fadeColor, (_b = gradientColors.first.to) !== null && _b !== void 0 ? _b : this.config.gradientOpacityTo));
            gradient.addColorStop(gradientColorBreakPoint, hexToRGB(gradientColors.second.fadeColor, (_c = gradientColors.second.to) !== null && _c !== void 0 ? _c : this.config.gradientOpacityTo));
            gradient.addColorStop(1, hexToRGB(gradientColors.second.fadeColor, (_d = gradientColors.second.from) !== null && _d !== void 0 ? _d : this.config.gradientOpacityFrom));
            dataset.backgroundColor = gradient;
            var gradientBorder = ctx.createLinearGradient(0, gradientStart, 0, gradientStop);
            gradientBorder.addColorStop(0, gradientColors.first.fadeColor);
            gradientBorder.addColorStop(gradientColorBreakPoint, gradientColors.first.fadeColor);
            gradientBorder.addColorStop(gradientColorBreakPoint, gradientColors.second.fadeColor);
            gradientBorder.addColorStop(1, gradientColors.second.fadeColor);
            dataset.borderColor = gradientBorder;
            dataset.pointBackgroundColor = gradientBorder;
        }
    };
    LineChartPlugin.prototype.getFillPointColor = function (chart, dataset) {
        var color = LEGEND_COLOR.get(chart.data.datasets[dataset._datasetIndex]);
        if (typeof color === 'string') {
            return color;
        }
        else {
            if (isFadeColor(color)) {
                return color.fadeColor;
            }
            if (isTrendColor(color)) {
                return color.primaryColor.fadeColor;
            }
            if (isMixedColor(color)) {
                return dataset._chart.data.datasets[dataset._datasetIndex].data[dataset._index] >= 0
                    ? color.first.fadeColor
                    : color.second.fadeColor;
            }
        }
    };
    LineChartPlugin.prototype.isActive = function (context) {
        return this.lastActivePoints.some(function (e) { return e._datasetIndex == context.datasetIndex && e._index == context.dataIndex; });
    };
    return LineChartPlugin;
}());

var TargetAlignMode;
(function (TargetAlignMode) {
    TargetAlignMode["Center"] = "center";
    TargetAlignMode["Left"] = "left";
    TargetAlignMode["Right"] = "right";
})(TargetAlignMode || (TargetAlignMode = {}));

var TargetPlugin = /** @class */ (function () {
    function TargetPlugin(userConfig) {
        var _a, _b;
        this.id = 'cdsChartTarget';
        this.config = {
            align: TargetAlignMode.Left,
            textColor: CHART_COLORS.darkBlue,
            font: "bold 14px " + (((_b = (_a = Chart === null || Chart === void 0 ? void 0 : defaults) === null || _a === void 0 ? void 0 : _a.global) === null || _b === void 0 ? void 0 : _b.defaultFontFamily) || 'sans'),
            lineColor: CHART_COLORS.darkBlue,
            lineWidth: 2,
            paddingHorizontal: 20,
            paddingVertical: 5,
            textBottom: '',
            textTop: '',
            value: null,
        };
        this.config = __assign(__assign({}, this.config), userConfig);
    }
    TargetPlugin.prototype.afterInit = function (chart) {
        this.chart = chart;
        chart.ctx.font = this.config.font;
        if (!chart.ctx.measureText('').actualBoundingBoxAscent) {
            var ie11Font = document.createElement('span');
            ie11Font.style.font = this.config.font;
            ie11Font.style.display = 'none';
            document.body.appendChild(ie11Font);
            this.ieTextSize = Number(getComputedStyle(ie11Font).fontSize.replace(/[^\d.]*/g, ''));
            document.body.removeChild(ie11Font);
        }
    };
    TargetPlugin.prototype.afterDraw = function (chart) {
        if (isChartScale(chart)) {
            var _a = chart.scales['y-axis-0'], min = _a.min, max = _a.max;
            var ctx = chart.ctx, _b = chart.chartArea, left = _b.left, right = _b.right, top_1 = _b.top, bottom = _b.bottom;
            var _c = this.config, paddingVertical = _c.paddingVertical, value = _c.value, lineColor = _c.lineColor, textColor = _c.textColor, lineWidth = _c.lineWidth, textBottom = _c.textBottom, textTop = _c.textTop;
            var maxTickAbs = Math.abs(max);
            var verticalChartAreaPx = bottom - top_1;
            var verticalChartAreaUnits = -min + max;
            var valuePx = ((maxTickAbs - value) * verticalChartAreaPx) / verticalChartAreaUnits;
            var paddingWithHalfLineWidth = paddingVertical + lineWidth / 2;
            var _d = this.calculateTextAlignPosition(), align = _d.align, textTopPositionX = _d.textTopPositionX, textBottomPositionX = _d.textBottomPositionX;
            var linePosition = this.config.lineWidth % 2 !== 0
                ? Math.round(top_1 + valuePx) + 0.5
                : Math.round(top_1 + valuePx);
            if (linePosition >= top_1 - this.config.lineWidth / 2 && linePosition <= bottom + this.config.lineWidth / 2) {
                this.drawLine({
                    ctx: ctx,
                    lineColor: lineColor,
                    left: left,
                    linePosition: linePosition,
                    right: right
                });
                this.drawTexts({
                    ctx: ctx,
                    textColor: textColor,
                    align: align,
                    textTop: textTop,
                    textBottom: textBottom,
                    linePosition: linePosition,
                    paddingWithHalfLineWidth: paddingWithHalfLineWidth,
                    top: top_1,
                    textTopPositionX: textTopPositionX,
                    textBottomPositionX: textBottomPositionX,
                    bottom: bottom
                });
            }
        }
    };
    TargetPlugin.prototype.drawLine = function (_a) {
        var ctx = _a.ctx, lineColor = _a.lineColor, left = _a.left, linePosition = _a.linePosition, right = _a.right;
        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = this.config.lineWidth;
        ctx.moveTo(left, linePosition);
        ctx.lineTo(right, linePosition);
        ctx.stroke();
        ctx.restore();
    };
    TargetPlugin.prototype.drawTexts = function (_a) {
        var ctx = _a.ctx, textColor = _a.textColor, align = _a.align, textTop = _a.textTop, textBottom = _a.textBottom, linePosition = _a.linePosition, paddingWithHalfLineWidth = _a.paddingWithHalfLineWidth, top = _a.top, textTopPositionX = _a.textTopPositionX, textBottomPositionX = _a.textBottomPositionX, bottom = _a.bottom;
        ctx.save();
        ctx.beginPath();
        ctx.font = this.config.font;
        ctx.fillStyle = textColor;
        ctx.textAlign = align;
        var _b = ctx.measureText(textTop), _c = _b.actualBoundingBoxAscent, textTopABBA = _c === void 0 ? this.ieTextSize : _c, _d = _b.actualBoundingBoxDescent, textTopABBD = _d === void 0 ? 0 : _d;
        var _e = ctx.measureText(textBottom), _f = _e.actualBoundingBoxAscent, textBottomABBA = _f === void 0 ? this.ieTextSize : _f, _g = _e.actualBoundingBoxDescent, textBottomABBD = _g === void 0 ? 0 : _g;
        var textTopHeight = textTopABBA + textTopABBD;
        var textBottomHeight = textBottomABBA + textBottomABBD;
        if (textTop && linePosition - textTopHeight - paddingWithHalfLineWidth < top) {
            ctx.textBaseline = 'hanging';
            var _h = ctx.measureText(textBottom).actualBoundingBoxAscent, textBottomABBA_1 = _h === void 0 ? 0 : _h;
            ctx.fillText(textTop, textTopPositionX, linePosition + paddingWithHalfLineWidth + textBottomABBA_1);
            ctx.fillText(textBottom, textBottomPositionX, linePosition + 2 * paddingWithHalfLineWidth + textBottomHeight + textBottomABBA_1);
        }
        else if (textTop && textBottom && linePosition + textBottomHeight + paddingWithHalfLineWidth > bottom) {
            ctx.fillText(textTop, textTopPositionX, linePosition - 2 * paddingWithHalfLineWidth - textTopHeight - textBottomABBD);
            ctx.fillText(textBottom, textBottomPositionX, linePosition - paddingWithHalfLineWidth - textBottomABBD);
        }
        else if (!textTop && textBottom && linePosition + textBottomHeight + paddingWithHalfLineWidth > bottom) {
            ctx.fillText(textBottom, textBottomPositionX, linePosition - paddingWithHalfLineWidth - textBottomABBD);
        }
        else {
            ctx.fillText(textTop, textTopPositionX, linePosition - paddingWithHalfLineWidth - textTopABBD);
            ctx.textBaseline = 'hanging';
            var _j = ctx.measureText(textBottom).actualBoundingBoxAscent, textBottomABBA_2 = _j === void 0 ? 0 : _j;
            ctx.fillText(textBottom, textBottomPositionX, linePosition + paddingWithHalfLineWidth + textBottomABBA_2);
        }
        ctx.restore();
    };
    TargetPlugin.prototype.calculateTextAlignPosition = function () {
        var align;
        var textTopPositionX;
        var textBottomPositionX;
        switch (this.config.align) {
            case TargetAlignMode.Left: {
                align = TargetAlignMode.Left;
                textTopPositionX = this.chart.chartArea.left + this.config.paddingHorizontal;
                textBottomPositionX = textTopPositionX;
                break;
            }
            case TargetAlignMode.Center: {
                var center = (this.chart.chartArea.left + this.chart.chartArea.right) / 2;
                align = TargetAlignMode.Center;
                textTopPositionX = center;
                textBottomPositionX = center;
                break;
            }
            case TargetAlignMode.Right: {
                align = TargetAlignMode.Right;
                textTopPositionX = this.chart.chartArea.right - this.config.paddingHorizontal;
                textBottomPositionX = textTopPositionX;
                break;
            }
        }
        return { align: align, textTopPositionX: textTopPositionX, textBottomPositionX: textBottomPositionX };
    };
    TargetPlugin.prototype.update = function (userConfig) {
        this.config = __assign(__assign({}, this.config), userConfig);
        this.afterInit(this.chart);
    };
    return TargetPlugin;
}());

var TooltipPointColorMode;
(function (TooltipPointColorMode) {
    TooltipPointColorMode["First"] = "first";
    TooltipPointColorMode["Mixed"] = "mixed";
    TooltipPointColorMode["Auto"] = "auto";
})(TooltipPointColorMode || (TooltipPointColorMode = {}));

var TooltipPlugin = /** @class */ (function () {
    function TooltipPlugin(userConfig) {
        this.config = {
            customContent: null,
            labelTransform: null,
            pointColorMode: TooltipPointColorMode.Mixed,
            valueTransform: null
        };
        this.config = __assign(__assign({}, this.config), userConfig);
        this.tooltipElementTemplate = this.createTemplate();
    }
    TooltipPlugin.prototype.beforeInit = function (chart) {
        var _this = this;
        chart.config.options.tooltips.enabled = false;
        chart.config.options.tooltips.custom = function (tooltipModel) {
            var _a, _b;
            if (isChartTooltip(chart)) {
                if (_this.popperTooltipInstance) {
                    _this.popperTooltipInstance.destroy();
                }
                var index_1 = ((_b = (_a = chart === null || chart === void 0 ? void 0 : chart.tooltip) === null || _a === void 0 ? void 0 : _a._active[0]) === null || _b === void 0 ? void 0 : _b._index) || 0;
                var isIndexMode = chart.options.tooltips.mode === 'index';
                var isCanvas = chart.canvas.parentElement.classList.contains('cds-chartjs-wrapper');
                if (!isCanvas) {
                    throw new Error('Chart should have "cds-chartjs-wrapper" class on his parent');
                }
                var canvasWrapper = chart.canvas.parentElement;
                var tooltipParent = canvasWrapper.querySelector('.cds-chartjs-tooltip-parent');
                var tooltipEl = canvasWrapper.querySelector('.cds-chartjs-tooltip');
                var arrow = canvasWrapper.querySelector('.cds-chartjs-tooltip__arrow');
                if (!arrow) {
                    arrow = document.createElement('div');
                    arrow.classList.add('cds-chartjs-tooltip__arrow');
                    arrow.innerHTML = '<div><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8"><path d="M3 0h10l-5 5z"/></svg></div>';
                }
                if (!tooltipEl) {
                    tooltipEl = document.createElement('div');
                    tooltipEl.classList.add('cds-chartjs-tooltip');
                    canvasWrapper.appendChild(tooltipEl);
                }
                if (!_this.config.customContent) {
                    var children_1 = tooltipEl.getElementsByClassName('cds-chartjs-tooltip__element');
                    var childrenLength = children_1.length;
                    var tooltipElementsAmount = chart.data.datasets.reduce(function (prev, curr) { return prev + (curr.data[index_1] != null ? 1 : 0); }, 0);
                    if (childrenLength < tooltipElementsAmount) {
                        for (var i = 0; i < tooltipElementsAmount - childrenLength; i++) {
                            tooltipEl.appendChild(_this.tooltipElementTemplate.cloneNode(true));
                        }
                    }
                    else if (childrenLength > tooltipElementsAmount) {
                        for (var i = 0; i < childrenLength - tooltipElementsAmount; i++) {
                            tooltipEl.removeChild(children_1[childrenLength - 1 - i]);
                        }
                    }
                    var childIndex_1 = 0;
                    chart.data.datasets.forEach(function (dataset) {
                        if (dataset.data[index_1] != null) {
                            _this.updateTooltipElement(children_1[childIndex_1++], index_1, dataset);
                        }
                    });
                }
                else {
                    tooltipEl.innerHTML = '';
                    tooltipEl.appendChild(_this.config.customContent(chart.data.datasets, index_1));
                }
                if (!tooltipParent) {
                    tooltipParent = document.createElement('div');
                    tooltipParent.classList.add('cds-chartjs-tooltip-parent');
                    if (!isIndexMode) {
                        tooltipParent.classList.add('cds-chartjs-tooltip-parent__offset-25');
                    }
                    canvasWrapper.appendChild(tooltipParent);
                }
                tooltipEl.appendChild(arrow);
                tooltipParent.style.left = tooltipModel.caretX + "px";
                tooltipParent.style.top = isIndexMode
                    ? chart.chartArea.top + "px"
                    : tooltipModel.caretY + "px";
                tooltipEl.style.pointerEvents = 'none';
                tooltipParent.style.pointerEvents = 'none';
                _this.popperTooltipInstance = createPopper(tooltipParent, tooltipEl, {
                    placement: isIndexMode ? 'top' : 'right',
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top', 'right', 'left'],
                            },
                        },
                        {
                            name: 'arrow',
                            options: {
                                element: arrow,
                            }
                        },
                    ],
                });
                if (tooltipModel.opacity === 0) {
                    _this.popperTooltipInstance.destroy();
                    tooltipEl.style.display = 'none';
                }
                else {
                    tooltipEl.style.display = 'block';
                }
            }
        };
    };
    TooltipPlugin.prototype.destroy = function () {
        this.popperTooltipInstance = null;
    };
    TooltipPlugin.prototype.createTemplate = function () {
        var template = document.createElement('div');
        template.classList.add('cds-chartjs-tooltip__element');
        var circle = document.createElement('span');
        circle.classList.add('cds-chartjs-tooltip__circle');
        var label = document.createElement('span');
        label.classList.add('cds-chartjs-tooltip__label');
        var value = document.createElement('span');
        value.classList.add('cds-chartjs-tooltip__value');
        template.appendChild(circle);
        template.appendChild(label);
        template.appendChild(value);
        return template;
    };
    TooltipPlugin.prototype.updateTooltipElement = function (element, index, dataset) {
        var circle = element.querySelector('.cds-chartjs-tooltip__circle');
        var label = element.querySelector('.cds-chartjs-tooltip__label');
        var value = element.querySelector('.cds-chartjs-tooltip__value');
        circle.style.cssText = this.getTooltipCircleStyle(index, dataset);
        label.innerText = this.config.labelTransform ? this.config.labelTransform(dataset.label) : dataset.label;
        value.innerText = this.config.valueTransform ? this.config.valueTransform(dataset.data[index]) : String(dataset.data[index]);
    };
    TooltipPlugin.prototype.getTooltipCircleStyle = function (index, dataset) {
        var color = LEGEND_COLOR.get(dataset);
        if (typeof color === 'string') {
            return "background-color: " + color;
        }
        else {
            if (isFadeColor(color)) {
                return "background-color: " + color.fadeColor;
            }
            if (isMixedColor(color)) {
                if (this.config.pointColorMode === TooltipPointColorMode.First)
                    return "background-color: " + color.first.fadeColor;
                else if (this.config.pointColorMode === TooltipPointColorMode.Auto) {
                    return "background-color: " + (Number(dataset.data[index]) >= 0 ? color.first.fadeColor : color.second.fadeColor);
                }
                else {
                    var gradient = "linear-gradient(" + color.first.fadeColor + " 0%, " + color.first.fadeColor + " 50%, " + color.second.fadeColor + " 50%, " + color.second.fadeColor + " 100%)";
                    return "background-image: " + gradient;
                }
            }
        }
    };
    return TooltipPlugin;
}());

export { BarChartPlugin, CDS_CHARTJS_COLORS, DoughnutChartPlugin, GridMode, GridPlugin, LegendPlugin, LegendPointColorMode, LineChartPlugin, TargetAlignMode, TargetPlugin, TooltipPlugin, TooltipPointColorMode, useBarChart, useDoughnutChart };
